import { captureClient } from "@v2/monitor/providers/sentry-init";

import { AUTH_MANAGER_CHANGE_EVENT, AUTH_STORAGE_KEY } from "./config";

import type { ApiResponse, AuthCredentials, AuthUser } from "../types";

import { InvalidCredentialsError } from "./errors/invalid-credentials";

/**
 * Captura um erro de auth com contexto ÚTIL e SEM PII sensível (nunca
 * token/senha). É o que dá visibilidade ao deslogamento em massa (#159).
 * Usa a abstração `captureClient` de `@v2/monitor` (não importa o SDK direto).
 */
function capturarAuth(
  step: "refresh" | "login" | "microsoft",
  error: unknown,
  extra?: Record<string, unknown>,
): void {
  captureClient(error, { tags: { area: "auth", step }, extra });
}

/**
 * Gerencia `AuthCredentials` (tokens + identidademínima)
 *
 */
export class AuthManager {
  private static instance: AuthManager | null = null;
  private data: AuthCredentials | null = null;
  private readonly storageKey: string;
  private refreshing: Promise<boolean> | null = null;

  private constructor() {
    this.storageKey = AUTH_STORAGE_KEY;
    this.loadFromStorage();
    this.listenCrossTab();
  }

  /**
   * Sincroniza o token entre ABAS (#159). Quando OUTRA aba grava novas
   * credenciais (rotacionou o token), o evento `storage` dispara aqui: a aba
   * adota o token novo em memória e avisa a UI. Sem isto, uma aba ociosa
   * guardaria o refresh token já rotacionado e, ao tentar renovar, dispararia
   * a detecção de reuso no servidor — que revoga a família e desloga todo mundo.
   */
  private listenCrossTab(): void {
    if (typeof window === "undefined") return;
    // `AuthManager` é singleton e vive o ciclo inteiro da aba — o listener é
    // intencionalmente permanente (sem cleanup); some quando a aba fecha.
    window.addEventListener("storage", (e) => {
      if (e.key !== this.storageKey) return;
      this.loadFromStorage();
      this.dispatchChange();
    });
  }

  static getInstance(): AuthManager {
    if (!AuthManager.instance) AuthManager.instance = new AuthManager();
    return AuthManager.instance;
  }

  // ─── Leitura ───────────────────────────────────────────────────────────

  isAuthenticated(): boolean {
    const token = this.data?.accessJwt;
    if (!token) return false;
    return !this.isJwtExpired(token);
  }

  hasSession(): boolean {
    return this.data !== null;
  }

  getAccessToken(): string | null {
    return this.data?.accessJwt ?? null;
  }

  getRefreshToken(): string | null {
    return this.data?.refreshToken ?? null;
  }

  getUser(): AuthUser | null {
    return this.data?.user ?? null;
  }

  getCredentials(): AuthCredentials | null {
    return this.data;
  }

  /**
   * ms até o ponto de refresh PROATIVO (default 75% do TTL do access token),
   * calculado de `iat`/`exp` do JWT. `null` se não há token; `0` se já passou
   * do ponto (renovar já). O scheduler do provider usa isto pra renovar ANTES
   * de expirar — é o que mata o logout ocioso (#159).
   */
  msUntilRefresh(fraction = 0.75): number | null {
    const token = this.data?.accessJwt;
    if (!token) return null;
    const t = this.jwtTimes(token);
    if (t?.exp == null) return null;
    const nowSec = Date.now() / 1000;
    const iat = t.iat ?? nowSec;
    const ttl = t.exp - iat;
    if (ttl <= 0) return 0;
    // Renova a `fraction` do TTL, MAS sempre antes da expiração efetiva
    // (exp − leeway), com uma folga — garante o refresh antes de o token virar
    // inválido, inclusive em TTLs curtos (repro a 30/60s).
    const BUFFER_S = 2;
    const refreshAtSec = Math.min(
      iat + ttl * fraction,
      t.exp - AuthManager.CLOCK_SKEW_S - BUFFER_S,
    );
    return Math.max((refreshAtSec - nowSec) * 1000, 0);
  }

  // ─── Mutação ───────────────────────────────────────────────────────────

  setCredentials(data: AuthCredentials): void {
    this.data = data;
    this.persist(data);
    this.dispatchChange();
  }

  clear(): void {
    this.data = null;
    this.persist(null);
    this.dispatchChange();
  }

  async login(email: string, password: string): Promise<void> {
    let response: Response;
    try {
      response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
    } catch (error) {
      capturarAuth("login", error, { reason: "network" });
      throw new Error(`Erro de rede no login: ${(error as Error).message}`);
    }

    // 401 = credenciais inválidas (esperado, não é incidente) → não captura.
    if (response.status === 401) {
      throw new InvalidCredentialsError();
    }

    const envelope = (await response
      .json()
      .catch(() => null)) as ApiResponse<AuthCredentials> | null;

    if (!response.ok || !envelope || envelope.ok === false) {
      const message =
        envelope && envelope.ok === false
          ? envelope.error.message
          : `Login retornou HTTP ${response.status}`;
      capturarAuth("login", new Error(message), {
        httpStatus: response.status,
        code: envelope && envelope.ok === false ? envelope.error.code : undefined,
      });
      throw new Error(message);
    }

    this.setCredentials(envelope.data);
  }

  async startMicrosoftLogin(callbackURL: string): Promise<string> {
    let response: Response;
    try {
      response = await fetch("/api/auth/sign-in/social", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ provider: "microsoft", callbackURL }),
      });
    } catch (error) {
      capturarAuth("microsoft", error, { phase: "start", reason: "network" });
      throw new Error(
        `Erro de rede ao iniciar login Microsoft: ${(error as Error).message}`,
      );
    }

    if (!response.ok) {
      capturarAuth(
        "microsoft",
        new Error(`Falha ao iniciar login Microsoft (HTTP ${response.status})`),
        { phase: "start", httpStatus: response.status },
      );
      throw new Error(
        `Falha ao iniciar login Microsoft (HTTP ${response.status})`,
      );
    }

    const data = (await response.json().catch(() => null)) as {
      url?: string;
    } | null;

    if (!data?.url) {
      capturarAuth(
        "microsoft",
        new Error("Resposta inesperada do servidor ao iniciar login Microsoft"),
        { phase: "start", reason: "no-url" },
      );
      throw new Error(
        "Resposta inesperada do servidor ao iniciar login Microsoft",
      );
    }

    return data.url;
  }

  async logout(): Promise<void> {
    const refreshToken = this.getRefreshToken();
    this.clear();
    if (!refreshToken) return;
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
    } catch {
      // best effort
    }
  }

  async refreshAccessToken(): Promise<boolean> {
    // Single-flight POR ABA: várias chamadas concorrentes compartilham 1 refresh.
    if (this.refreshing) return this.refreshing;
    this.refreshing = this.refreshCoordinated().finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  /**
   * Single-flight CROSS-TAB (#159). Só UMA aba renova por vez via Web Locks;
   * dentro do lock re-lê o storage — se outra aba já renovou nesse meio-tempo,
   * adota o token novo em vez de mandar o refresh token já rotacionado (o que
   * dispararia a detecção de reuso no servidor → revogação da família → logout
   * em massa). Sem Web Locks (browser antigo), degrada pro fluxo direto.
   */
  private async refreshCoordinated(): Promise<boolean> {
    const run = async (): Promise<boolean> => {
      this.loadFromStorage();
      const current = this.data?.accessJwt;
      if (current && !this.isJwtExpired(current)) {
        // Outra aba já renovou — só propaga, não bate no servidor.
        this.dispatchChange();
        return true;
      }
      return this.doRefresh();
    };

    if (
      typeof navigator !== "undefined" &&
      "locks" in navigator &&
      navigator.locks
    ) {
      return navigator.locks.request("agros-auth-refresh", run);
    }
    return run();
  }

  private async doRefresh(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;

    let response: Response;
    try {
      response = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
    } catch (error) {
      // TRANSITÓRIO (rede/blip) → NÃO desloga; mantém a sessão pra tentar de novo.
      capturarAuth("refresh", error, { reason: "network-transient" });
      return false;
    }

    if (response.ok) {
      const envelope = (await response
        .json()
        .catch(() => null)) as ApiResponse<AuthCredentials> | null;
      if (envelope && envelope.ok !== false) {
        this.setCredentials(envelope.data);
        return true;
      }
      capturarAuth(
        "refresh",
        new Error(
          envelope && envelope.ok === false
            ? envelope.error.message
            : "Refresh sem envelope válido",
        ),
        {
          reason: "invalid-envelope",
          code: envelope && envelope.ok === false ? envelope.error.code : undefined,
        },
      );
      this.clear();
      return false;
    }

    // TRANSITÓRIO (5xx/429/408) → mantém a sessão. DEFINITIVO (401/invalid/reused/
    // expired → demais 4xx) → desloga, pois o refresh token está realmente morto.
    const transitorio =
      response.status >= 500 ||
      response.status === 429 ||
      response.status === 408;
    capturarAuth(
      "refresh",
      new Error(`Refresh falhou (HTTP ${response.status})`),
      { httpStatus: response.status, reason: transitorio ? "transient" : "definitive" },
    );
    if (!transitorio) this.clear();
    return false;
  }

  private loadFromStorage(): void {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      this.data = raw ? (JSON.parse(raw) as AuthCredentials) : null;
    } catch {
      this.data = null;
    }
  }

  private persist(data: AuthCredentials | null): void {
    if (typeof window === "undefined") return;
    if (data) {
      window.localStorage.setItem(this.storageKey, JSON.stringify(data));
    } else {
      window.localStorage.removeItem(this.storageKey);
    }
  }

  private dispatchChange(): void {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(AUTH_MANAGER_CHANGE_EVENT));
  }

  /** Tolerância de clock skew (s): trata o token como expirado um pouco antes
   *  do `exp` real, evitando mandar um token "no limite" que o servidor recusa.
   *  Pequeno de propósito (deve ser << TTL pra não brigar com o refresh proativo). */
  private static readonly CLOCK_SKEW_S = 10;

  /** Extrai `iat`/`exp` (segundos) do payload do JWT, sem verificar assinatura. */
  private jwtTimes(token: string): { iat?: number; exp?: number } | null {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) return null;
      const payload = JSON.parse(this.decodeBase64Url(parts[1])) as {
        iat?: number;
        exp?: number;
      };
      return { iat: payload.iat, exp: payload.exp };
    } catch {
      return null;
    }
  }

  private isJwtExpired(token: string): boolean {
    const t = this.jwtTimes(token);
    if (typeof t?.exp !== "number") return true;
    // Leeway: expira CLOCK_SKEW_S antes do exp real.
    return Date.now() / 1000 >= t.exp - AuthManager.CLOCK_SKEW_S;
  }
  private decodeBase64Url(input: string): string {
    const base64 = input.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    return decodeURIComponent(
      binary
        .split("")
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join(""),
    );
  }
}
